import { PIN_LENGTH } from '@/lib/enrolmentSession';

export interface PinDotsProps {
  /** How many digits have been entered. Never the digits themselves — nothing needs them to draw this. */
  readonly entered: number;
}

/**
 * The six slots [Figma · `PIN dots`, frames 01–04].
 *
 * Read from the file: six 16px circles 16px apart, a 2px `--border` ring when empty and filled with
 * `--primary` when entered — `02 · Entering` draws the first two filled and the rest outlined.
 *
 * ⛔ **Decorative, and hidden from assistive technology.** A row of circles announced one by one says
 * nothing; the live region beside it reports the count in words, which is what a staffer using a screen
 * reader actually needs [AC-10]. ⛔ And the digits themselves are never rendered: the frame masks them and
 * an unattended lobby tablet is the wrong place to echo a site's PIN back at head height.
 */
export function PinDots({ entered }: PinDotsProps) {
  return (
    <>
      <div aria-hidden="true" className="flex items-center gap-4">
        {Array.from({ length: PIN_LENGTH }, (_, slot) => (
          <span
            className={`size-4 rounded-full border-2 border-solid ${
              slot < entered
                ? 'border-[var(--primary)] bg-[var(--primary)]'
                : 'border-[var(--border)]'
            }`}
            key={slot}
          />
        ))}
      </div>
      <p aria-live="polite" className="sr-only">
        {`${String(entered)} of ${String(PIN_LENGTH)} digits entered`}
      </p>
    </>
  );
}
